import type React from "react";
import { useEffect, useState } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  getShipment,
  clearCurrentShipment,
  updateShipment,
  createShipment,
  getNextShipmentId,
  assignShipmentToDriverApp,
  removeShipmentFromDriverApp,
  clearError,
} from "../../store/slices/shipmentSlice";
import { withVersion } from "../../utils/concurrency";
import type {
  Shipment,
  CreateShipmentData,
  ShipmentType,
  TransportMode,
} from "../../types";
import { ShipmentForm } from "../../components/ShipmentForm/ShipmentForm";
import { useTranslation } from "react-i18next";

export const ShipmentPage: React.FC = () => {
  const { t } = useTranslation("shipments");
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // When navigated here from "Copy" on the shipments list, the sanitized
  // prefill data (driver-tracking / waybill / attachments / comments already
  // stripped server-side) arrives via router state, not a fetch.
  // "Create Shipment" on an SO/PO order uses the same channel and adds
  // `sourceOrder` so the new shipment gets linked back to the order.
  const routeState = location.state as {
    copyData?: Partial<Shipment>;
    sourceOrder?: {
      id: string;
      number: string;
      type?: string;
      rawIncoterm?: string | null;
    };
  } | null;
  const copyData = routeState?.copyData;
  const sourceOrder = routeState?.sourceOrder;

  const { currentShipment, loading, error } = useAppSelector(
    (state) => state.shipment,
  );

  const [nextShipmentId, setNextShipmentId] = useState<string>("");
  const [pageLoading, setPageLoading] = useState(true);
  const [shipmentType, setShipmentType] = useState<ShipmentType | null>(null);
  const [initialTransportMode, setInitialTransportMode] =
    useState<TransportMode | null>(null);

  // Determine if this is edit mode or create mode
  const isEditMode = id !== "new";

  // Get transport mode (and type) from URL params for new shipments.
  // The Add Shipment dropdown now picks a transport mode (Road / Sea / Air);
  // Road shipments default to an International (CMR) type.
  useEffect(() => {
    const modeParam = searchParams.get("transportMode");
    const typeParam = searchParams.get("type");

    if (modeParam === "ROAD" || modeParam === "SEA" || modeParam === "AIR") {
      setInitialTransportMode(modeParam);
    } else if (!isEditMode) {
      setInitialTransportMode("ROAD");
    }

    if (typeParam === "International" || typeParam === "Domestic") {
      setShipmentType(typeParam);
    } else if (!isEditMode) {
      // Default to International if no type specified for new shipments
      setShipmentType("International");
    }
  }, [searchParams, isEditMode]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Load shipment data
  useEffect(() => {
    const loadData = async () => {
      setPageLoading(true);
      try {
        if (isEditMode && id) {
          // Edit mode: fetch existing shipment
          dispatch(clearCurrentShipment());
          await dispatch(getShipment(id)).unwrap();
        } else {
          // Create mode: get next shipment ID
          dispatch(clearCurrentShipment());
          const response = await dispatch(getNextShipmentId()).unwrap();
          setNextShipmentId(response);
        }
      } catch (err) {
        console.error("Error loading shipment:", err);
        toast.error(t("page.failedToLoad"));
        navigate("/shipments");
      } finally {
        setPageLoading(false);
      }
    };

    loadData();

    // Cleanup on unmount
    return () => {
      dispatch(clearCurrentShipment());
    };
  }, [id, isEditMode, dispatch, navigate]);

  const handleCreateShipment = async (data: CreateShipmentData) => {
    try {
      // Ensure the shipment type is set from URL param
      const shipmentData = {
        ...data,
        // Link back to the SO/PO order this shipment was created from
        ...(sourceOrder ? { sourceOrderId: sourceOrder.id } : {}),
      };
      await dispatch(createShipment({ shipment: shipmentData })).unwrap();
      toast.success(t("page.createdSuccess"));
      navigate("/shipments");
      return true;
    } catch (err) {
      console.error("Error creating shipment:", err);
      return false;
    }
  };

  const handleUpdateShipment = async (data: Partial<Shipment>) => {
    if (!currentShipment) return false;

    try {
      await dispatch(
        updateShipment({
          id: currentShipment._id,
          shipmentData: withVersion(data, currentShipment) as any,
        }),
      ).unwrap();
      toast.success(t("page.updatedSuccess"));
      return true;
    } catch (err) {
      console.error("Error updating shipment:", err);
      if (typeof err === "string") toast.error(err);
      return false;
    }
  };

  const handleAssignToDriver = async () => {
    if (!currentShipment) return;
    await dispatch(assignShipmentToDriverApp(currentShipment._id)).unwrap();
  };

  const handleUnassignFromDriver = async () => {
    if (!currentShipment) return;
    await dispatch(removeShipmentFromDriverApp(currentShipment._id)).unwrap();
  };

  const handleGoBack = () => {
    navigate("/shipments");
  };

  // Show loading state
  if (pageLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  // Show error state for edit mode when shipment not found
  if (isEditMode && !currentShipment && !loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <p className="text-dark-600 mb-4">{t("page.shipmentNotFound")}</p>
        <button
          onClick={handleGoBack}
          className="inline-flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>{t("page.backToShipments")}</span>
        </button>
      </div>
    );
  }

  // Determine shipment number to display
  const shipmentNumber = isEditMode
    ? currentShipment?.shipmentNumber
    : nextShipmentId;

  // Get type label for display
  // const getTypeLabel = () => {
  //   if (isEditMode && currentShipment) {
  //     return currentShipment.type === "International"
  //       ? "International (CMR)"
  //       : "Domestic (Inland)";
  //   }
  //   return shipmentType === "International"
  //     ? "International (CMR)"
  //     : "Domestic (Inland)";
  // };

  return (
    <div className="h-full flex flex-col">
      {/* Form Content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <ShipmentForm
          shipment={currentShipment || (copyData as Shipment) || undefined}
          isCopySource={!isEditMode && !!copyData}
          sourceOrder={sourceOrder}
          onSubmit={(data: any) => {
            if (isEditMode && currentShipment) {
              return handleUpdateShipment(data as Partial<Shipment>);
            } else {
              return handleCreateShipment(data as CreateShipmentData);
            }
          }}
          loading={loading}
          isEditMode={isEditMode && !!currentShipment}
          shipmentNumber={shipmentNumber || ""}
          onAssign={handleAssignToDriver}
          onUnassign={handleUnassignFromDriver}
          isAssigned={!!currentShipment?.assignedToDriverApp}
          initialType={shipmentType}
          initialTransportMode={initialTransportMode}
          handleGoBack={handleGoBack}
        />
      </div>
    </div>
  );
};

export default ShipmentPage;
